import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from './icons/ChevronDownIcon'

export interface CardProps {
  /** DOM id on the card element — a stable hook for id-scoped CSS (e.g. `#mcpcfg-tenant-card`). */
  id?: string
  title: React.ReactNode
  subtitle?: string
  actions?: React.ReactNode
  children: React.ReactNode
  defaultCollapsed?: boolean
  locked?: boolean
  noPadding?: boolean
  className?: string
  collapsible?: boolean
}

export function Card({ id, title, subtitle, actions, children, defaultCollapsed = false, locked = false, noPadding = false, className, collapsible = true }: CardProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  useEffect(() => {
    if (locked) setCollapsed(defaultCollapsed)
  }, [defaultCollapsed, locked])
  const { t } = useTranslation()
  const headingId = useId()

  const toggleCollapse = () => {
    if (!locked && collapsible) {
      setCollapsed(c => !c)
    }
  }

  return (
    <div id={id} className={`card${collapsed ? ' collapsed' : ''}${className ? ` ${className}` : ''}`} data-locked={locked ? 'true' : undefined}>
      <div
        className="card-header"
        onClick={collapsible ? toggleCollapse : undefined}
        style={{
          cursor: collapsible && !locked ? 'pointer' : 'default',
          transition: 'background .15s',
        }}
        onKeyDown={collapsible && !locked ? (e) => e.key === 'Enter' && toggleCollapse() : undefined}
      >
        <h3 id={headingId}>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
        {collapsible && (
          <div className="card-header-actions" onClick={(e) => e.stopPropagation()}>
            {actions}
            <button
              type="button"
              className="card-collapse-btn"
              aria-labelledby={headingId}
              aria-expanded={!collapsed}
              title={locked ? t('card.locked') : (collapsed ? t('card.expand') : t('card.collapse'))}
              onClick={(e) => {
                e.stopPropagation()
                if (!locked) setCollapsed(c => !c)
              }}
              disabled={locked}
              style={{
                transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                opacity: locked ? 0.35 : 1,
                pointerEvents: locked ? 'none' : 'auto',
                cursor: locked ? 'not-allowed' : 'pointer',
              }}
            >
              <ChevronDownIcon />
            </button>
          </div>
        )}
        {!collapsible && actions && (
          <div className="card-header-actions" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </div>
      {noPadding ? children : <div className="card-body">{children}</div>}
    </div>
  )
}
